import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Media, SnakeToCamelObject } from '@browsing/api';
import { BellFilledXS, BellOutlinedXS } from '@lux/zds-icons';
import { Preset, Text } from '@lux/text/tw';
import { cn } from '@lux/core';
import { Translate } from '@pegasus/translation';

import { UpcomingCampaign } from '@shared/types/mylounge';

interface UpcomingCampaignCardMiniProps {
    campaign: UpcomingCampaign;
    media: Omit<SnakeToCamelObject<Media>, 'path'>;
    bgTransparent?: boolean;
    isPlusVariant?: boolean;
    isSubscribed?: boolean;
    onToggleSubscription?: (campaignId: string) => Promise<void>;
    onClick?: () => void;
    onView?: () => void;
}

const UpcomingCampaignCardMini = ({
    campaign,
    media,
    bgTransparent = false,
    isPlusVariant = false,
    isSubscribed = false,
    onToggleSubscription,
    onView,
}: UpcomingCampaignCardMiniProps) => {
    const { campaignId } = campaign;
    const { ref, inView } = useInView({
        threshold: 0.5,
        triggerOnce: true,
    });

    useEffect(() => {
        if (inView && onView) {
            onView();
        }
    }, [inView, onView]);

    const handleNotifyClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onToggleSubscription) {
            await onToggleSubscription(campaignId);
        }
    };

    return (
        <div
            id={`upcoming-mini-${campaignId}`}
            className={cn(
                'relative flex h-full w-full flex-col overflow-hidden transition-all duration-[330ms] ease-in-out',
                bgTransparent ? 'bg-transparent' : 'bg-function-0',
            )}
            ref={ref}
        >
            <div className="flex h-full w-full overflow-hidden">
                <img
                    className="m-auto h-full w-full object-contain"
                    src={media.url}
                    alt={media.altText}
                    loading="lazy"
                />
            </div>
            {isPlusVariant && onToggleSubscription && (
                <div className="py-s px-xs flex flex-col">
                    <Text preset={Preset.S} bold className="truncate">
                        {campaign.name}
                    </Text>
                    <button
                        type="button"
                        className={cn(
                            'h-l mt-xs flex w-full cursor-pointer items-center justify-center rounded-full border',
                            isSubscribed
                                ? 'bg-function-5 border-function-5 sm:hover:bg-function-10 sm:hover:border-function-10'
                                : 'bg-function-0 border-function-100 sm:hover:bg-function-5 sm:hover:border-function-5',
                        )}
                        onClick={handleNotifyClick}
                    >
                        {isSubscribed ? (
                            <BellFilledXS className="mr-xxs" />
                        ) : (
                            <BellOutlinedXS className="mr-xxs" />
                        )}
                        <Text preset={Preset.S} bold className="truncate">
                            {isSubscribed ? (
                                <Translate message="subscribed" />
                            ) : (
                                <Translate message="notsubscribed" />
                            )}
                        </Text>
                    </button>
                </div>
            )}
        </div>
    );
};

export { UpcomingCampaignCardMini };
